/**
 * saleTicketEscPos.ts — Bytes ESC/POS alineados al TicketPreview (texto + logo opcional).
 * Codificación Latin-1 para tildes/ñ en térmicas; sin emojis. Logo: raster GS v 0 si hay data URL.
 */

import type { CartItem, Producto } from "@/types";
import type {
  AnulacionVenta,
  Devolucion,
  PaymentBreakdown,
  Venta,
} from "@/features/caja/types/caja.types";
import {
  renderItemsTableEscPos,
  toEscPosTableItems,
} from "@/features/caja/utils/escposItemsTable";
import { cartItemsToTicketLines } from "@/features/caja/utils/ticketLineBreakdown";
import { appendTaxLinesToTicket } from "@/features/caja/utils/cartDiscountUtils";
import type {
  DiscountBreakdown,
  LineDiscount,
} from "@/features/caja/utils/discountHelper";
import {
  getTicketMaxWidth,
  sanitizeForThermal,
  wrapThermalText as wrapHard,
  type ThermalPreviewLine,
  type ThermalPreviewSection,
} from "@/features/caja/utils/escposLayout";
import { decodeEscPosToPreviewLines } from "@/features/caja/utils/escPosPlainTextDecode";
import { buildQrCodeEscPos } from "@/lib/escPosBarcode";
import {
  buildSaleTicketUsageFeeRows,
  resolveSaleTicketPaymentLines,
  saleTicketNetIngresoTotal,
  saleTicketUsageFeeLabel,
} from "@/features/caja/utils/paymentMethodUsageFee";
import {
  rebuildDiscountBreakdownFromVenta,
  rebuildGlobalDiscountFromVenta,
  ventaProductoListPrice,
} from "@/features/caja/utils/ventaDiscountReplay";
import {
  computeVentaTotalOriginal,
  rebuildOriginalCartFromVenta,
  ventaHasPostSaleAdjustments,
} from "@/features/caja/utils/ventaTicketReplay";
import { sumAnulacionesMonto } from "@/features/caja/utils/applyAnulacionVenta";
import { businessConfigService } from "@/features/caja/services/businessConfig.service";
import type { TicketFormato } from "@/features/caja/utils/businessConfigStorage";
import {
  buildSaleTicketCompactPlainTextLines,
  saleTicketSupportsCompact,
} from "@/features/caja/utils/saleTicketCompactText";
import {
  buildGlobalDetailHeaderLines,
  buildGlobalFooterLines,
  buildGlobalHeaderLines,
  buildGlobalNotaLines,
  buildGlobalPaymentLines,
  buildGlobalPuntosLines,
  buildGlobalTotalsLines,
  GLOBAL_THIN_RULE,
} from "@/features/caja/utils/saleTicketGlobalSections";
import { buildSaleTicketUltraCompactPlainTextLines } from "@/features/caja/utils/saleTicketUltraCompactText";

const ESC = 0x1b;
const GS = 0x1d;

/** Mismo default que TicketPreview (evita campos vacíos si LS tiene strings en blanco). */
export interface BusinessInfo {
  nombre: string;
  slogan: string;
  direccion: string;
  nit: string;
  sitioWeb: string;
  whatsapp: string;
  mensajeGracias: string;
  logo?: string;
  logoThermal?: string;
  ticketQrUrl?: string;
  ticketQrLabel?: string;
  /** Formato térmico elegido por el negocio: "completo" (rejillas) o "compacto" (ahorro papel). */
  formatoTicket?: TicketFormato;
  /** Tarifa por defecto para desglosar impuesto en ítems sin tarifa propia. */
  impuestoDefaultPct?: number;
}

const DEFAULT_BUSINESS: BusinessInfo = {
  nombre: "ALTENOT",
  slogan: "Sistema de Control Total",
  direccion: "Calle Falsa 123",
  nit: "123456789-0",
  sitioWeb: "www.altenot.com",
  whatsapp: "+57 314 274 9518",
  mensajeGracias: "¡GRACIAS POR TU COMPRA!",
  ticketQrUrl: "",
  ticketQrLabel: "",
  formatoTicket: "completo",
};

export function loadBusinessForReceipt(): BusinessInfo {
  try {
    return businessConfigService.get(DEFAULT_BUSINESS);
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_BUSINESS };
}

/**
 * URL efectiva del QR del pie del ticket.
 * 1) `ticketQrUrl` configurada; 2) `sitioWeb` del negocio (con https:// si falta);
 * 3) dominio de la plataforma ALTENOT como último fallback — idéntico al pie de
 * texto que ya imprime `altentot.com` cuando el negocio no define sitio. Así el
 * QR del ticket siempre queda visible en la demo y en negocios sin web propia.
 * Cuando exista el ticket digital por venta (ruta pública /t/:venta), esta
 * función se reemplaza por esa URL sin tocar el resto del código.
 */
export function resolveTicketQrUrl(biz: {
  ticketQrUrl?: string;
  sitioWeb?: string;
}): string {
  const direct = biz.ticketQrUrl?.trim();
  if (direct) return direct;
  const site = biz.sitioWeb?.trim();
  const target = site || "www.altentot.com";
  return /^https?:\/\//i.test(target) ? target : `https://${target}`;
}

function u8(...bytes: number[]): Uint8Array {
  return new Uint8Array(bytes);
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const n = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Tildes/ñ para impresoras en code page Latin-1 / CP1252 común */
const UNICODE_TO_LATIN1: Record<string, number> = {
  "\u00c1": 0xc1,
  "\u00c9": 0xc9,
  "\u00cd": 0xcd,
  "\u00d3": 0xd3,
  "\u00da": 0xda,
  "\u00dc": 0xdc,
  "\u00d1": 0xd1,
  "\u00e1": 0xe1,
  "\u00e9": 0xe9,
  "\u00ed": 0xed,
  "\u00f3": 0xf3,
  "\u00fa": 0xfa,
  "\u00fc": 0xfc,
  "\u00f1": 0xf1,
  "\u00a1": 0xa1,
  "\u00bf": 0xbf,
  "\u00b0": 0xb0,
};

function stripUnsupported(s: string): string {
  return s
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u2600-\u26FF]/g, "");
}

function encodeReceiptText(s: string): Uint8Array {
  const cleaned = stripUnsupported(s.replace(/\u20ac/g, "EUR")).normalize(
    "NFC",
  );
  const bytes: number[] = [];
  for (const ch of cleaned) {
    const code = ch.codePointAt(0) ?? 0x3f;
    if (code <= 0xff) {
      bytes.push(code);
      continue;
    }
    const mapped = UNICODE_TO_LATIN1[ch];
    if (mapped !== undefined) bytes.push(mapped);
    else bytes.push(0x3f);
  }
  return new Uint8Array(bytes);
}

function lineLatin1(s: string): Uint8Array {
  return concat(encodeReceiptText(s), u8(0x0a));
}

function feedLines(n: number): Uint8Array {
  const parts: Uint8Array[] = [];
  for (let i = 0; i < n; i++) parts.push(u8(0x0a));
  return concat(...parts);
}

/** Base64 seguro en navegador para buffers grandes */
export function uint8ToBase64(buf: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < buf.length; i += CHUNK) {
    const sub = buf.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, Array.from(sub) as number[]);
  }
  return btoa(binary);
}

function formatMethod(method: string, fallbackLabel?: string): string {
  if (fallbackLabel && fallbackLabel.trim()) return fallbackLabel.trim();
  const m: Record<string, string> = {
    efectivo: "Efectivo",
    tarjeta: "Tarjeta",
    nequi: "Nequi",
    daviplata: "Daviplata",
    transferencia: "Transferencia",
    digital_1: "Digital 1",
    digital_2: "Digital 2",
    digital_3: "Digital 3",
    digital_4: "Digital 4",
    mixto: "Pago Mixto",
    credito: "Venta a crédito",
  };
  return m[method] || method;
}

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("es-CO")}`;
}

function pushTwoColToLines(
  lines: string[],
  left: string,
  right: string,
  width: number,
): void {
  for (const ln of twoColLineStrings(left, right, width)) {
    lines.push(sanitizeForThermal(ln));
  }
}

function twoColLineStrings(
  left: string,
  right: string,
  width: number,
): string[] {
  const L = left.replace(/\s+/g, " ").trimEnd();
  const R = right.replace(/\s+/g, " ").trim();
  if (L.length + R.length + 1 <= width) {
    return [L + " ".repeat(width - L.length - R.length) + R];
  }
  return [L.slice(0, width), " ".repeat(Math.max(0, width - R.length)) + R];
}

const MAX_LOGO_HEIGHT = 140;

/** Convierte un data URL de imagen a raster ESC/POS (GS v 0). */
async function imageDataUrlToEscPosRaster(
  dataUrl: string,
  targetWidthDots: number,
): Promise<Uint8Array | null> {
  if (typeof Image === "undefined" || typeof document === "undefined")
    return null;
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("logo"));
      img.src = dataUrl;
    });
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (!natW || !natH) return null;
    let w = Math.min(targetWidthDots, natW);
    let h = Math.round((natH * w) / natW);
    if (h > MAX_LOGO_HEIGHT) {
      h = MAX_LOGO_HEIGHT;
      w = Math.max(8, Math.round((natW * h) / natH));
    }
    w = Math.max(8, Math.min(w, targetWidthDots));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    return monoToGsV0Raster(data, w, h);
  } catch {
    return null;
  }
}

/** GS v 0 m=0 raster bit image */
function monoToGsV0Raster(
  rgba: Uint8ClampedArray,
  w: number,
  h: number,
): Uint8Array {
  const widthBytes = Math.ceil(w / 8);
  const xL = widthBytes & 0xff;
  const xH = (widthBytes >> 8) & 0xff;
  const yL = h & 0xff;
  const yH = (h >> 8) & 0xff;
  const rows: number[] = [];
  for (let y = 0; y < h; y++) {
    for (let b = 0; b < widthBytes; b++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = b * 8 + bit;
        let black = 0;
        if (x < w) {
          const i = (y * w + x) * 4;
          const lum =
            0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
          black = lum < 168 ? 1 : 0;
        }
        byte |= black << (7 - bit);
      }
      rows.push(byte);
    }
  }
  const body = new Uint8Array(rows);
  return concat(u8(GS, 0x76, 0x30, 0x00, xL, xH, yL, yH), body);
}

export interface SaleTicketEscPosInput {
  cart: CartItem[];
  catalogProducts?: Producto[];
  total: number;
  subtotal?: number;
  subtotalBruto?: number;
  discountBreakdown?: DiscountBreakdown;
  globalDiscount?: LineDiscount | null;
  servicioPct?: number;
  servicioAmount?: number;
  /** Incentivo por método de pago (descuento al cliente) — fila en ticket. */
  incentivePct?: number;
  incentiveMethodLabel?: string;
  incentiveAmount?: number;
  /**
   * Forma de pago simple (método + valor en una línea) en vez de la tabla
   * MÉTODO | RECIB. | CAMBIO | VALOR. Usado por el ticket del pedido de Tienda.
   */
  formaPagoSimple?: boolean;
  paymentMethod: string;
  /** Etiqueta visible del método principal en el momento de cobrar (snapshot). */
  paymentMethodLabel?: string;
  paymentLines?: PaymentBreakdown[];
  cashReceived: number;
  change: number;
  note?: string;
  anulada?: boolean;
  devoluciones?: Devolucion[];
  /** Anulaciones parciales/total registradas sobre la venta (historial). */
  anulaciones?: AnulacionVenta[];
  /** Saldo activo del registro tras devoluciones/anulaciones (≠ total original). */
  saldoVigente?: number;
  clientePuntosNombre?: string;
  clientePuntosCodigo?: string;
  puntosGanados?: number | null;
  puntosNuevoSaldo?: number | null;
  puntosAnteriores?: number | null;
  premiosCanjeados?: { nombre: string; puntos: number }[];
  numeroVenta?: number | string;
  /** Punto de venta / caja (auditoría). */
  cajaNombre?: string;
  /** Empleado que abrió la sesión de caja. */
  sesionAbiertaPor?: string;
  /** Cajero que cobró (contraseña). */
  registradoPor?: string;
  /** Quién anuló (si aplica). */
  anuladoPor?: string;
  clienteCreditoNombre?: string;
  paperWidthMm: 58 | 80;
  ticketDate?: Date;
  /** Tarifa pasarela para el negocio — solo copia interna, no ticket al cliente. */
  showNegocioFeeAudit?: boolean;
  /**
   * Formato térmico deseado. Si no llega, se usa el de la config del negocio
   * (BusinessInfo.formatoTicket). El compacto solo aplica si
   * `saleTicketSupportsCompact` lo permite; en comprobantes con ajustes
   * post-venta/crédito/puntos el builder completo es el que manda.
   */
  formatoTicket?: TicketFormato;
  /**
   * Vista previa: muestra el formato pedido AUNQUE la venta no lo soporte
   * (puntos/crédito/ajustes). Solo afecta a la previsualización; la impresión
   * real JAMÁS debe activar este flag (ahí manda `saleTicketSupportsCompact`).
   */
  forzarFormatoEnPreview?: boolean;
}

function rasterTargetWidth(paperWidthMm: 58 | 80): number {
  return paperWidthMm === 80 ? 576 : 384;
}

/**
 * Ticket completo: init, logo opcional, texto con espaciado y pie como TicketPreview.
 */
export async function buildSaleTicketEscPosAsync(
  input: SaleTicketEscPosInput,
): Promise<Uint8Array> {
  const biz = loadBusinessForReceipt();
  const targetW = rasterTargetWidth(input.paperWidthMm);
  const parts: Uint8Array[] = [];

  parts.push(u8(ESC, 0x40));

  const logo = (biz.logoThermal || biz.logo)?.trim();
  if (logo && logo.startsWith("data:image")) {
    parts.push(u8(ESC, 0x61, 0x01));
    const raster = await imageDataUrlToEscPosRaster(logo, targetW);
    if (raster) {
      parts.push(raster);
      parts.push(feedLines(2));
    }
    parts.push(u8(ESC, 0x61, 0x00));
  }

  parts.push(buildReceiptTextContent(input, biz));

  // Dynamic QR Code at the bottom of the thermal ticket — comando NATIVO
  // GS ( k (modelo 2): no depende del DOM/canvas y siempre imprime grande.
  const qrUrl = resolveTicketQrUrl(biz);
  if (qrUrl) {
    parts.push(u8(ESC, 0x61, 0x01)); // Center
    if (biz.ticketQrLabel?.trim()) {
      const encoder = new TextEncoder();
      parts.push(
        encoder.encode(sanitizeForThermal(biz.ticketQrLabel.trim()) + "\n\n"),
      );
    }
    parts.push(buildQrCodeEscPos(qrUrl, { moduleSize: 6 }));
    parts.push(u8(ESC, 0x61, 0x00)); // Reset alignment
    parts.push(feedLines(2));
  }

  // Corte al FINAL (después del QR): todo queda en la misma hoja.
  parts.push(u8(GS, 0x56, 0x41, 0x10));
  return concat(...parts);
}

/** Sin logo; mismo texto (útil si no hay DOM). */
export function buildSaleTicketEscPos(
  input: SaleTicketEscPosInput,
): Uint8Array {
  const biz = loadBusinessForReceipt();
  return concat(
    u8(ESC, 0x40),
    buildReceiptTextContent(input, biz),
    u8(GS, 0x56, 0x41, 0x10),
  );
}

/**
 * Líneas de texto plano idénticas a las que imprime la APK (sin bytes ESC/POS).
 * Usado por el simulador térmico en TicketPreview.
 */
export function buildSaleTicketPlainTextLines(
  input: SaleTicketEscPosInput,
  biz?: BusinessInfo,
): string[] {
  const b = biz ?? loadBusinessForReceipt();
  // Precedencia: 1) pedido explícito del caller; 2) config pasada (biz); 3) config
  // persistida del negocio. Así la impresión y todos los previews (caja, tienda,
  // historial) respetan SIEMPRE la misma decisión del negocio.
  const formato =
    input.formatoTicket ??
    b.formatoTicket ??
    loadBusinessForReceipt().formatoTicket ??
    "completo";
  // La regla de seguridad (compacto/ultra solo en ventas estándar) aplica a la
  // IMPRESIÓN. En vista previa (`forzarFormatoEnPreview`) se respeta la elección
  // del usuario para que pueda ver cómo se vería, aunque el ticket real se
  // imprima Completo (puntos/crédito/ajustes).
  const soportaCompacto =
    saleTicketSupportsCompact(input) || input.forzarFormatoEnPreview === true;
  if (formato === "compacto" && soportaCompacto) {
    return buildSaleTicketCompactPlainTextLines(input, b);
  }
  if (formato === "ultracompacto" && soportaCompacto) {
    return buildSaleTicketUltraCompactPlainTextLines(input, b);
  }

  const lines: string[] = [];
  const maxW = getTicketMaxWidth(input.paperWidthMm);
  const emit = (s: string) => lines.push(sanitizeForThermal(s));
  const blank = (n = 1) => {
    for (let i = 0; i < n; i++) lines.push("");
  };
  const sepMinor = () => emit(GLOBAL_THIN_RULE.repeat(maxW));

  if (input.anulada) {
    emit("VENTA ANULADA");
    if (input.anuladoPor?.trim()) emit(`Por: ${input.anuladoPor.trim()}`);
    blank();
  } else {
    const hasDev = (input.devoluciones?.length ?? 0) > 0;
    const hasAnu = (input.anulaciones?.length ?? 0) > 0;
    if (hasDev || hasAnu) {
      if (hasDev && hasAnu) {
        emit("REGISTRO CON AJUSTES");
        emit("Devoluciones y anulaciones");
      } else if (hasDev) {
        emit("REGISTRO CON DEVOLUCIONES");
      } else {
        emit("REGISTRO CON ANULACIONES");
      }
      const actorNames = [
        ...new Set(
          [
            ...(input.devoluciones ?? []).map((d) => d.userName?.trim()),
            ...(input.anulaciones ?? []).map((a) => a.userName?.trim()),
          ].filter((n): n is string => Boolean(n)),
        ),
      ];
      if (actorNames.length > 0) emit(`Por: ${actorNames.join(", ")}`);
      blank();
    }
  }

  // ── Cabecera global: idéntica en los 3 formatos (marco ==== + meta) ──────
  lines.push(...buildGlobalHeaderLines(input, b));

  // ── DETALLE DE VENTA (título común) — las filas son por formato ─────────
  lines.push(...buildGlobalDetailHeaderLines(input));

  const ticketBreakdownOpts = input.catalogProducts?.length
    ? { catalog: input.catalogProducts }
    : undefined;
  const ticketLinesBase = cartItemsToTicketLines(
    input.cart,
    ticketBreakdownOpts,
  );
  // Sin descuentos por línea en el detalle: cada descuento vive una sola vez
  // en la sección de Totales (mismo criterio web/térmica). La tarifa por defecto
  // se resuelve igual que en la Vista Web (storage del negocio), para que los
  // ítems sin tarifa propia muestren su IVA por ítem también en térmica.
  const ticketLines = appendTaxLinesToTicket(
    ticketLinesBase,
    input.cart,
    b.impuestoDefaultPct ?? loadBusinessForReceipt().impuestoDefaultPct ?? 0,
  );
  renderItemsTableEscPos({
    items: toEscPosTableItems(ticketLines),
    paperWidthMm: input.paperWidthMm,
    emitLine: emit,
    money,
    wrap: wrapHard,
    thermal: true,
    showIndex: true,
  });
  sepMinor();

  // ── Totales + forma de pago + nota (globales, igual en los 3 formatos) ──
  lines.push(...buildGlobalTotalsLines(input));
  lines.push(...buildGlobalPaymentLines(input));
  lines.push(...buildGlobalNotaLines(input));

  // ── Bloques forenses SOLO del formato completo (ajustes post-venta) ─────
  const ticketPayLines = resolveSaleTicketPaymentLines({
    paymentMethod: input.paymentMethod,
    total: input.total,
    paymentLines: input.paymentLines,
  });
  const usageFeeRows = buildSaleTicketUsageFeeRows(ticketPayLines, (id, snap) =>
    formatMethod(id, snap),
  );

  if (input.paymentMethod === "credito" && input.clienteCreditoNombre?.trim()) {
    emit("CLIENTE CREDITO");
    emit(input.clienteCreditoNombre.trim());
    sepMinor();
  }

  const negocioFeeRows = usageFeeRows.filter((r) => r.paidBy === "negocio");
  if (input.showNegocioFeeAudit && negocioFeeRows.length > 0) {
    sepMinor();
    emit("TARIFA PASARELA (USO INTERNO)");
    for (const row of negocioFeeRows) {
      pushTwoColToLines(
        lines,
        `${saleTicketUsageFeeLabel(row, "tarifa")}:`,
        `-${money(row.feeAmount)}`,
        maxW,
      );
    }
    pushTwoColToLines(
      lines,
      "Neto ingreso negocio:",
      money(saleTicketNetIngresoTotal(ticketPayLines, input.total)),
      maxW,
    );
  }

  if (input.devoluciones?.length) {
    sepMinor();
    emit(`DEVOLUCIONES (${input.devoluciones.length})`);
    blank();
    let sumDev = 0;
    for (const dev of input.devoluciones) {
      sumDev += dev.montoReembolsado;
      emit(
        `${new Date(dev.fecha).toLocaleString("es-CO", { day: "numeric", month: "short" })} -${money(dev.montoReembolsado)}`,
      );
      emit(`${formatMethod(dev.metodoPago)} · ${dev.motivoDevolucion}`);
      if (dev.userName?.trim()) emit(`  Por: ${dev.userName.trim()}`);
      for (const item of dev.items) {
        emit(`  - ${item.nombre}: ${item.cantidad} ud.`);
      }
    }
    pushTwoColToLines(lines, "TOTAL DEVUELTO:", `-${money(sumDev)}`, maxW);
    blank();
  }

  if (input.anulaciones?.length) {
    sepMinor();
    emit(`ANULACIONES (${input.anulaciones.length})`);
    blank();
    let sumAnu = 0;
    for (const anu of input.anulaciones) {
      sumAnu += anu.montoAnulado;
      emit(
        `${new Date(anu.fecha).toLocaleString("es-CO", { day: "numeric", month: "short" })} -${money(anu.montoAnulado)}`,
      );
      emit(`  ${anu.motivoAnulacion}`);
      if (anu.userName?.trim()) emit(`  Por: ${anu.userName.trim()}`);
      for (const item of anu.items) {
        emit(`  - ${item.nombre}: ${item.cantidad} ud.`);
      }
    }
    pushTwoColToLines(lines, "TOTAL ANULADO:", `-${money(sumAnu)}`, maxW);
    emit("(Correccion de registro — no reembolso)");
    blank();
  }

  if (input.saldoVigente != null && input.saldoVigente !== input.total) {
    sepMinor();
    pushTwoColToLines(lines, "SALDO VIGENTE:", money(input.saldoVigente), maxW);
    blank();
  }

  // Programa de puntos (formato completo; los puntos exigen formato completo)
  const puntosLines = buildGlobalPuntosLines(input);
  if (puntosLines.length > 0) {
    sepMinor();
    lines.push(...puntosLines);
    sepMinor();
  } else if (!input.note?.trim()) {
    // Sin nota ni puntos: regla fina antes del pie para no quedar pegado.
    sepMinor();
  }

  const qrLabelText = b.ticketQrLabel ?? "";
  lines.push(...buildGlobalFooterLines(b, maxW, qrLabelText));
  blank(2);

  return lines;
}

/** Texto plano unificado para preview térmico (join con \\n). */
export function buildSaleTicketPlainText(
  input: SaleTicketEscPosInput,
  biz?: BusinessInfo,
): string {
  return buildSaleTicketPlainTextLines(input, biz).join("\n");
}

/** Negrita en títulos/totales (preview e impresión). */
export function shouldBoldSaleLine(
  line: string,
  section: ThermalPreviewSection,
  headerTextLineIndex: number,
): boolean {
  const t = line.trim();
  if (!t) return false;
  if (section === "header" && headerTextLineIndex === 1) return true;
  if (
    /^VENTA ANULADA|^REGISTRO CON (DEVOLUCIONES|ANULACIONES|AJUSTES)/i.test(t)
  )
    return true;
  if (/^DETALLE DE VENTA$/i.test(t)) return true;
  if (/^TOTALES DE VENTA$/i.test(t)) return true;
  if (/^COMPROBANTE DE VENTA$/i.test(t)) return true;
  if (/^FORMA DE PAGO$/i.test(t)) return true;
  if (/^NOTA$/i.test(t)) return true;
  if (/^CLIENTE CREDITO$/i.test(t)) return true;
  if (/^DEVOLUCIONES/i.test(t)) return true;
  if (/^ANULACIONES/i.test(t)) return true;
  if (/^SALDO VIGENTE:/i.test(t)) return true;
  if (/^TOTAL ANULADO:/i.test(t)) return true;
  if (/^PROGRAMA DE PUNTOS$/i.test(t)) return true;
  if (/^SUBTOTAL:/i.test(t)) return true;
  if (/^Descuento General/i.test(t)) return true;
  if (/^Base Gravable:/i.test(t)) return true;
  if (/^(IVA|Impoconsumo)(?: incluido)? \(/i.test(t)) return true;
  if (/^SERVICIO \(/i.test(t)) return true;
  if (/^TOTAL A PAGAR/i.test(t)) return true;
  if (/^TOTAL PAGADO:|^TOTAL CREDITO/i.test(t)) return true;
  if (/^TOTAL DEVUELTO:/i.test(t)) return true;
  if (/^Concepto/i.test(t) && /Valor/i.test(t)) return true;
  if (/^CANT/i.test(t) && /PRODUCTO|PROD/i.test(t) && /TOTAL|TOT/i.test(t))
    return true;
  if (/^Cant/i.test(t) && /Producto|Prod/i.test(t) && /Total|Tot/i.test(t))
    return true;
  if (/^Gestionado con$/i.test(t)) return true;
  if (/^ALTENOT$/i.test(t)) return true;
  return false;
}

function appendThermalBlock(
  parts: Uint8Array[],
  lines: string[],
  section: ThermalPreviewSection,
  defaultAlign: "left" | "center",
  shouldBold: typeof shouldBoldSaleLine,
): void {
  if (lines.length === 0) return;

  let alignState: "left" | "center" | null = null;
  let boldState = false;
  let headerTextLineIndex = 0;

  const setAlign = (align: "left" | "center") => {
    if (alignState === align) return;
    parts.push(u8(ESC, 0x61, align === "center" ? 0x01 : 0x00));
    alignState = align;
  };

  const setBold = (on: boolean) => {
    if (boldState === on) return;
    parts.push(u8(ESC, 0x45, on ? 0x01 : 0x00));
    boldState = on;
  };

  setAlign(defaultAlign);

  for (const line of lines) {
    const trimmed = line.trim();
    const lineAlign = /^_+$/.test(trimmed) ? "left" : defaultAlign;
    setAlign(lineAlign);

    const nextHeaderIdx =
      section === "header" && trimmed
        ? headerTextLineIndex + 1
        : headerTextLineIndex;
    setBold(shouldBold(line, section, nextHeaderIdx));
    if (section === "header" && trimmed) {
      headerTextLineIndex += 1;
    }

    parts.push(lineLatin1(line));
  }

  setBold(false);
}

function buildReceiptTextContent(
  input: SaleTicketEscPosInput,
  biz: BusinessInfo,
): Uint8Array {
  // El ticket de venta gestiona su propio espaciado (sin aire automático antes
  // de reglas `_`/`=`): se pasan las líneas tal cual para que el marco `====`
  // y las finas `----` queden pegadas al contenido (ahorro de papel).
  const lines = buildSaleTicketPlainTextLines(input, biz);
  const bodyStart = lines.findIndex((l) =>
    /^DETALLE DE VENTA$/i.test(l.trim()),
  );
  const footerStart = lines.findIndex((l) => /^¡?\s*GRACIAS/i.test(l.trim()));

  const headerLines = bodyStart >= 0 ? lines.slice(0, bodyStart) : lines;
  const bodyLines =
    bodyStart >= 0 && footerStart >= 0
      ? lines.slice(bodyStart, footerStart)
      : bodyStart >= 0
        ? lines.slice(bodyStart)
        : [];
  const footerLines = footerStart >= 0 ? lines.slice(footerStart) : [];

  const parts: Uint8Array[] = [];
  appendThermalBlock(
    parts,
    headerLines,
    "header",
    "center",
    shouldBoldSaleLine,
  );
  appendThermalBlock(parts, bodyLines, "body", "left", shouldBoldSaleLine);
  appendThermalBlock(
    parts,
    footerLines,
    "footer",
    "center",
    shouldBoldSaleLine,
  );
  // El corte lo agrega el builder (después del QR), no aquí: así el QR queda
  // en la misma hoja del ticket y no se imprime después de cortar.
  return concat(...parts);
}

/** Preview térmico 1:1 con bytes ESC/POS del ticket de venta. */
export function buildSaleTicketPreviewLines(
  input: SaleTicketEscPosInput,
  biz?: BusinessInfo,
): ThermalPreviewLine[] {
  const b = biz ?? loadBusinessForReceipt();
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const bytes = concat(u8(ESC, 0x40), buildReceiptTextContent(input, b));
  return decodeEscPosToPreviewLines(bytes, cols);
}

export function saleTicketPayloadFromVenta(
  v: Venta,
): Omit<SaleTicketEscPosInput, "paperWidthMm"> {
  const discountBreakdown = rebuildDiscountBreakdownFromVenta(v);
  const globalDiscount = rebuildGlobalDiscountFromVenta(v);
  const hasAdjustments = ventaHasPostSaleAdjustments(v);
  const montoAnulado = sumAnulacionesMonto(v);
  const totalOriginal = computeVentaTotalOriginal(v);
  const subtotalBruto =
    discountBreakdown?.subtotalBruto ??
    v.subtotal ??
    totalOriginal - (v.servicioAmount ?? 0);

  const cart = hasAdjustments
    ? rebuildOriginalCartFromVenta(v)
    : v.productos.map((p) => {
        // precioUnitario persistido = precio cobrado real; precioLista = lista
        // si la línea tenía descuento/precio especial (si no, ambos coinciden).
        const cobrado = p.precioUnitario;
        const lista = ventaProductoListPrice(v, p);
        return {
          id: p.productoId,
          nombre: p.nombre,
          precio: cobrado,
          precioLista: lista,
          precioMayorista: p.precioMayorista,
          quantity: p.cantidad,
          esMayorista: p.esMayorista,
          esVIP: p.esVIP,
          vipDescuentoPct: p.vipDescuentoPct,
          tieneIVA: p.tieneIVA,
          porcentajeIVA: p.porcentajeIVA,
          peso: p.peso,
          precioPorKilo: p.precioPorKilo,
          variantesSeleccionadas: p.variantesSeleccionadas,
          variantKey: p.variantKey,
          skuVariante: p.skuVariante,
          modificadoresSeleccionados: p.modificadoresSeleccionados,
          modifierKey: p.modifierKey,
          stock: 0,
          categoria: "",
          estado: "activo" as const,
          businessId: "",
          costo: 0,
          stockMinimo: 0,
          createdAt: "",
          updatedAt: "",
        } as CartItem;
      });

  const subtotalBase = v.subtotal ?? v.total - (v.servicioAmount ?? 0);
  const subtotal =
    hasAdjustments && montoAnulado > 0 && typeof v.subtotal === "number"
      ? v.subtotal + montoAnulado
      : subtotalBase;

  const totalDevuelto = (v.devoluciones ?? []).reduce(
    (acc, d) => acc + d.montoReembolsado,
    0,
  );

  let saldoVigente: number | undefined;
  if (hasAdjustments) {
    if (montoAnulado > 0) {
      saldoVigente = v.total;
    } else if (totalDevuelto > 0) {
      saldoVigente = Math.max(0, totalOriginal - totalDevuelto);
    }
  }

  return {
    cart,
    total: hasAdjustments ? totalOriginal : v.total,
    subtotal,
    subtotalBruto,
    discountBreakdown,
    globalDiscount,
    servicioPct: v.servicioPct ?? 0,
    servicioAmount: v.servicioAmount ?? 0,
    incentivePct: v.descuentos?.incentivoMetodoPago?.pct,
    incentiveMethodLabel: v.descuentos?.incentivoMetodoPago?.metodoLabel,
    incentiveAmount: v.descuentos?.incentivoMetodoPago?.descuentoTotal,
    paymentMethod: v.metodoPago,
    paymentMethodLabel: v.metodoPagoLabel,
    paymentLines: v.detalleMetodoPago?.breakdown,
    cashReceived: v.recibido ?? totalOriginal,
    change: v.cambio ?? 0,
    note: v.nota,
    anulada: v.estado === "anulada",
    devoluciones: v.devoluciones,
    anulaciones: v.anulaciones,
    saldoVigente,
    clientePuntosNombre: v.clientePuntosNombre,
    clientePuntosCodigo: v.clientePuntosCodigo,
    puntosGanados: v.puntosGanados ?? null,
    puntosNuevoSaldo: v.puntosNuevoSaldo ?? null,
    puntosAnteriores: v.saldoAntesCanje ?? null,
    premiosCanjeados: v.premiosCanjeados,
    numeroVenta: v.numeroOrden,
    ticketDate: v.fecha ? new Date(v.fecha) : undefined,
    cajaNombre: v.puntoCajaNombre,
    sesionAbiertaPor: v.sesionAbiertaPorNombre,
    registradoPor: v.userName,
    anuladoPor: v.estado === "anulada" ? v.anuladaPor : undefined,
    clienteCreditoNombre: v.clienteCreditoNombre,
  };
}
